import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HvProvider } from '@hitachivantara/uikit-react-core';
import {
  unstable_HistoryRouter as HistoryRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router';
import { AuthorizationProvider, useAuth } from '@iq/react-components';
import config from '../../config';

import {
  requestGrants,
  getGrants,
  authorizeUser,
  setCurrentUser,
  hasCheckedInvitations,
  getIsTenantOrSuperAdmin,
  getHasPermission,
  hasDocumentationReadPermission,
} from '../../bundles/auth';
import { getActiveSite, requestSites, getSitesLoaded } from '../../bundles/sites';
import { requestInstructionals } from '../../bundles/files';
import { getOnline } from '../../bundles/notifications';
import { requestComponents } from '../../bundles/components';
import { requestVisualizations } from '../../bundles/visualizations';
import { requestModels } from '../../bundles/models';
import { requestStateSets } from '../../bundles/statesets';
import { syncNotificationConfigs } from '../../bundles/notification-config';

import BaseView from '../BaseView';

import SiteType from '../SiteType';
import SiteDetailView from '../SiteDetailView';
import SiteAdminView from '../SiteAdminView';
import LayoutView from '../SiteAdminView/LayoutView';
import IntegrationView from '../SiteAdminView/IntegrationView';
import QrGenerationView from '../QrGenerationView';
import SourceView from '../SiteAdminView/SourceView';
import VariableView from '../SiteAdminView/VariableView';
import ComponentView from '../SiteAdminView/ComponentView';
import AttributeView from '../SiteAdminView/AttributeView';
import VisualizationView from '../SiteAdminView/VisualizationView';
import ModelView from '../SiteAdminView/ModelView';
import MemberView from '../SiteAdminView/MemberView';
import TagsView from '../SiteAdminView/TagsView';
import NotificationLandingView, { TransformerAlarmsView } from '../SiteAdminView/NotificationView';
import SupportView from '../SupportView';
import NoGrantsView from '../NoGrantsView';
import StateSetView from '../SiteAdminView/StateSetView';
import SiteView from '../SiteAdminView/SiteView';
import PageLastUpdated from '../PageHeader/PageLastUpdated';
import SourceConnectivityNotification from '../Notifications/SourceConnectivityNotification';
import GlobalMessagePortal from '../Notifications/GlobalMessagePortal';
import TopNotification from '../Notifications/TopNotification';
import AppContext from '../../AppContext';
import TenantAdminIntegrationView from '../TenantAdminView/components/TenantAdminIntegrationView';
import TenantAdminUserView from '../TenantAdminView/components/TenantAdminUserView/TenantAdminUserView';
import ApiDocumentView from '../ApiDocumentView/ApiDocumentView';
import ApiDocumentOverView from '../ApiDocumentView/ApiDocumentOverView';
import AnalyticsView from '../SiteAdminView/AnalyticsView';

const { AuthProvider } = AuthorizationProvider;

const NotFoundPage = () => (
  <div className="not-found">
    <h2>Sorry, page not found...</h2>
  </div>
);

const removeFirstLoader = () => {
  const loader = document.querySelector('#first-loader');
  if (loader) loader.remove();
};

const removeTrailingSlashes = (pathname) => {
  let end = pathname.length;
  while (end > 1 && pathname[end - 1] === '/') end--;
  return pathname.slice(0, end);
};

// NavLink `end` matching is exact on trailing slashes, so every URL is kept slash-less.
const RemoveTrailingSlash = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const normalizedPathname = removeTrailingSlashes(pathname);

  if (normalizedPathname !== pathname) {
    return (
      <Navigate
        to={{ pathname: normalizedPathname, search, hash }}
        replace
      />
    );
  }

  return children;
};

const Router = ({ history }) => {
  const dispatch = useDispatch();

  const { subject, realm, profile } = useAuth();

  const { email } = profile;
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingGrants, setIsLoadingGrants] = useState(true);

  const grants = useSelector(getGrants);
  const site = useSelector(getActiveSite);
  const sitesLoaded = useSelector(getSitesLoaded);

  const online = useSelector(getOnline);
  const invitesChecked = useSelector(hasCheckedInvitations);

  const { setTheme, theme } = useContext(AppContext);

  const setIqTheme = useCallback(() => {
    setTheme(theme);
  }, [theme]);

  const hvTheme = useMemo(() => (theme === 'dark' ? 'wicked' : null), [theme]);

  useEffect(() => {
    if (online && isLoadingGrants) {
      dispatch(
        requestGrants(subject, () => {
          setIsLoadingGrants(false);
        })
      );
    }
  }, [online, isLoadingGrants]);

  useEffect(() => {
    if (online && invitesChecked) {
      setIsLoadingGrants(true);
    }
  }, [online, invitesChecked]);

  const isTenantAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, realm));

  const canReadSites = useSelector((state) => getHasPermission(state, 'sites/Read'));

  const canWriteNotificationConfig = useSelector((state) =>
    site?.id
      ? getHasPermission(state, 'notificationConfig/Write', { org: site.org, site: site.id })
      : false
  );

  const canReadDocumentation = useSelector(hasDocumentationReadPermission);

  useEffect(() => {
    if (online && canReadSites) {
      dispatch(
        requestSites(() => {
          setIsLoadingSites(false);
        })
      );
      dispatch(requestInstructionals());
    }
  }, [online, canReadSites]);

  useEffect(() => {
    if (online && canReadSites && site?.id) {
      dispatch(requestComponents(site.id));
      dispatch(requestVisualizations(site.id));
      dispatch(requestModels(site.id));
      dispatch(requestStateSets(site.id));
      if (!config.onPremise && canWriteNotificationConfig) {
        dispatch(syncNotificationConfigs({ org: site.org, site: site.id }));
      }
    }
  }, [online, canReadSites, canWriteNotificationConfig, site.id, site.org, dispatch]);

  useEffect(() => {
    if (!config.onPremise) {
      dispatch(authorizeUser({ email, id: subject, org: realm }));
    }
    dispatch(setCurrentUser({ email, subject }));
  }, []);

  if (isLoadingGrants || (canReadSites && isLoadingSites)) {
    return (
      <div>
        {!online && (
          <GlobalMessagePortal>
            <TopNotification
              icon="he-nonetwork"
              message="No internet connection"
            />
          </GlobalMessagePortal>
        )}
      </div>
    );
  }

  if (
    history.location &&
    removeTrailingSlashes(history.location.pathname) !== '/sites' &&
    sitesLoaded
  ) {
    removeFirstLoader();
  }

  if (invitesChecked && !isLoadingGrants && (!grants || !canReadSites)) {
    removeFirstLoader();
    return <NoGrantsView />;
  }

  const siteDetailElement = (
    <BaseView
      site={site}
      withSidebar
      withRefresh
      headerChildren={<PageLastUpdated />}
      className="site-detail-view-component"
      withPadding={false}
      showBanner={true}
    >
      <SiteDetailView subject={subject} />
    </BaseView>
  );

  return (
    <HvProvider
      uiKitTheme={hvTheme}
      changeTheme={setIqTheme}
    >
      <div className={isLoadingGrants || isLoadingSites ? 'loading-main-content' : 'main-content'}>
        <HistoryRouter history={history}>
          <AuthProvider value={{ grants }}>
            <RemoveTrailingSlash>
              <Routes>
                {/* Pre-site routes */}
                <Route
                  path="/"
                  element={
                    <Navigate
                      to="/sites"
                      replace
                    />
                  }
                />
                <Route
                  path="/sites"
                  element={
                    <SiteType
                      sitesReady={!isLoadingSites}
                      realm={realm}
                      noSiteView
                    />
                  }
                />

                <Route
                  path="/api-docs/:id/*"
                  element={
                    <BaseView
                      title={{
                        type: 'string',
                        value: 'API Documentation',
                      }}
                      apiDocumentationView
                      noSitesView
                      className="docs-component"
                    >
                      <ApiDocumentView />
                    </BaseView>
                  }
                />

                <Route
                  path="/api-docs/*"
                  element={
                    canReadDocumentation ? (
                      <BaseView
                        title={{
                          type: 'string',
                          value: 'API Documentation',
                        }}
                        noSitesView
                        className="docs-component"
                        apiDocumentationView
                      >
                        <ApiDocumentOverView />
                      </BaseView>
                    ) : (
                      <Navigate
                        to="/sites"
                        replace
                      />
                    )
                  }
                />
                {!config.onPremise && (
                  <Route
                    path="/tenant-administration/users/*"
                    element={
                      isTenantAdmin ? (
                        <BaseView
                          title={{ type: 'string', value: 'Tenant Administration - Users' }}
                          noSitesView
                          className="manage-view"
                          isTenantAdminView
                        >
                          <TenantAdminUserView />
                        </BaseView>
                      ) : (
                        <Navigate
                          to="/sites"
                          replace
                        />
                      )
                    }
                  />
                )}
                {!config.onPremise && (
                  <Route
                    path="/tenant-administration/integrations/*"
                    element={
                      isTenantAdmin ? (
                        <BaseView
                          title={{ type: 'string', value: 'Tenant Administration - Integrations' }}
                          noSitesView
                          className="manage-view"
                          isTenantAdminView
                        >
                          <TenantAdminIntegrationView />
                        </BaseView>
                      ) : (
                        <Navigate
                          to="/sites"
                          replace
                        />
                      )
                    }
                  />
                )}
                {/* Admin - Settings - Data */}
                {!config.onPremise && (
                  <Route
                    path="/sites/:id/admin/integrations/*"
                    element={
                      <BaseView
                        siteAdminView
                        requiredPermission="integrations/Write"
                        trail={['Administration', 'Integrations']}
                      >
                        <IntegrationView />
                      </BaseView>
                    }
                  />
                )}
                <Route
                  path="/sites/:id/admin/sources/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="sources/Write"
                      trail={['Administration', 'Sources']}
                    >
                      <SourceView />
                    </BaseView>
                  }
                />
                <Route
                  path="/sites/:id/admin/variables/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="variables/Write"
                      trail={['Administration', 'Signals']}
                    >
                      <VariableView />
                    </BaseView>
                  }
                />
                <Route
                  path="/sites/:id/admin/components/qr/*"
                  element={<QrGenerationView />}
                />
                <Route
                  path="/sites/:id/admin/components/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="components/Write"
                      trail={['Administration', 'Components']}
                    >
                      <ComponentView />
                    </BaseView>
                  }
                />
                <Route
                  path="/sites/:id/admin/attributes/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="variables/Write"
                      trail={['Administration', 'Attributes']}
                    >
                      <AttributeView />
                    </BaseView>
                  }
                />

                <Route
                  path="/sites/:id/admin/statesets/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="statesets/Write"
                      trail={['Administration', 'Statesets']}
                    >
                      <StateSetView />
                    </BaseView>
                  }
                />
                <Route
                  path="/sites/:id/admin/visualizations/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="visualizations/Write"
                      trail={['Administration', 'Visualizations']}
                    >
                      <VisualizationView />
                    </BaseView>
                  }
                />
                <Route
                  path="/sites/:id/admin/analytics/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="sources/Write"
                      trail={['Administration', 'Analytics']}
                    >
                      <AnalyticsView />
                    </BaseView>
                  }
                />

                {/* Admin - Settings - General routes */}
                <Route
                  path="/sites/:id/admin/info/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="sites/Write"
                      trail={['Administration', 'Site']}
                    >
                      <SiteView />
                    </BaseView>
                  }
                />
                {!config.onPremise && (
                  <Route
                    path="/sites/:id/admin/members/*"
                    element={
                      <BaseView
                        siteAdminView
                        requiredPermission="members/Write"
                        trail={['Administration', 'Members']}
                      >
                        <MemberView />
                      </BaseView>
                    }
                  />
                )}
                {config.onPremise && (
                  <Route
                    path="/sites/:id/admin/members/*"
                    element={<NotFoundPage />}
                  />
                )}
                <Route
                  path="/sites/:id/admin/layout/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="sites/Write"
                    >
                      <LayoutView />
                    </BaseView>
                  }
                />
                {!config.onPremise && (
                  <Route
                    path="/sites/:id/admin/models/*"
                    element={
                      <BaseView
                        siteAdminView
                        requiredPermission="models/Write"
                        trail={['Administration', 'Models']}
                      >
                        <ModelView />
                      </BaseView>
                    }
                  />
                )}
                <Route
                  path="/sites/:id/admin/tags/*"
                  element={
                    <BaseView
                      siteAdminView
                      requiredPermission="tags/Write"
                      trail={['Administration', 'Tags']}
                    >
                      <TagsView />
                    </BaseView>
                  }
                />
                {!config.onPremise && (
                  <Route
                    path="/sites/:id/admin/notifications/transformer-alarms/*"
                    element={
                      <BaseView
                        siteAdminView
                        requiredPermission="notificationConfig/Write"
                        trail={['Administration', 'Notifications', 'Transformer Alarms']}
                      >
                        <TransformerAlarmsView />
                      </BaseView>
                    }
                  />
                )}
                {!config.onPremise && (
                  <Route
                    path="/sites/:id/admin/notifications"
                    element={
                      <BaseView
                        siteAdminView
                        requiredPermission="notificationConfig/Write"
                        trail={['Administration', 'Notifications']}
                      >
                        <NotificationLandingView />
                      </BaseView>
                    }
                  />
                )}
                {config.onPremise && (
                  <Route
                    path="/sites/:id/admin/notifications/*"
                    element={<NotFoundPage />}
                  />
                )}
                {/* Admin - Settings */}
                <Route
                  path="/sites/:id/admin"
                  element={
                    <BaseView
                      siteAdminView
                      site={site}
                      title={{ type: 'string', value: 'Administration' }}
                      className="settings-component"
                      withPadding={false}
                    >
                      <SiteAdminView />
                    </BaseView>
                  }
                />
                <Route
                  path="/sites/:id/admin/*"
                  element={<NotFoundPage />}
                />

                <Route
                  path="/sites/:id/support/*"
                  element={
                    <BaseView
                      site={site}
                      title={{ type: 'string', value: 'Support Information' }}
                      headerChildren={<p style={{ whiteSpace: 'nowrap' }}>Version 3.0</p>}
                      className="support-view-component"
                      withPadding={false}
                      withConnectivity={false}
                    >
                      <SupportView />
                    </BaseView>
                  }
                />

                <Route
                  path="/sites/:id"
                  element={siteDetailElement}
                />
                <Route
                  path="/sites/:id/:page/*"
                  element={siteDetailElement}
                />
                <Route
                  path="*"
                  element={<NotFoundPage />}
                />
              </Routes>
            </RemoveTrailingSlash>
          </AuthProvider>
        </HistoryRouter>
        {!online && (
          <GlobalMessagePortal site={site}>
            <TopNotification
              icon="he-nonetwork"
              message="No internet connection"
            />
          </GlobalMessagePortal>
        )}
        <GlobalMessagePortal>{site.id && <SourceConnectivityNotification />}</GlobalMessagePortal>
      </div>
    </HvProvider>
  );
};

export default Router;
